import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow
} from '@mui/material';
import { CustomTableFontCell } from 'components/CustomTableFontCell';
import { StyledTableSortLabel } from 'components/TableSortedLabel';
import moment from 'moment';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import TransactionFilter from '../../components/TransactionFilter';
import TransactionTypeChip from '../../components/TransactionTypeChip';
import { getAllTransactions } from '../../services/admin-organizations';
import { formatNumber } from '../../utils/utils';


const AdminTransactionsPage: React.FC = () => {
  const { t } = useTranslation();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [orderBy, setOrderBy] = useState('createdAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');
  const [filters, setFilters] = useState<{
    revisionId?: string;
    projectId?: string;
  }>({});

  const { data: transactions, isLoading, error } = useQuery({
    queryKey: ['transactions', orderBy, order, page, rowsPerPage, filters],
    queryFn: () => getAllTransactions({
      page: page + 1,
      pageSize: rowsPerPage,
      sortOrder: order,
      sortBy: orderBy,
      revisionId: filters.revisionId,
      projectId: filters.projectId
    })
  });

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleFilterChange = (newFilters: {
    revisionId?: string;
    projectId?: string;
  }) => {
    setFilters(newFilters);
    setPage(0); // Reset to first page when filters change
  };

  const formatDate = (date: string) => {
    return moment(date).format('DD/MM/YYYY HH:mm');
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
        <TransactionFilter onFilterChange={handleFilterChange} isLoading={isLoading} />
      </Box>
      
      <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '&:hover': { boxShadow: 'none' } }}>
                <CustomTableFontCell>
                  <StyledTableSortLabel
                    active={orderBy === 'createdAt'}
                    direction={orderBy === 'createdAt' ? order : 'asc'}
                    onClick={() => handleRequestSort('createdAt')}
                  >
                    {t('ADMIN_TRANSACTION_DATE')}
                  </StyledTableSortLabel>
                </CustomTableFontCell>
                <CustomTableFontCell>
                  <StyledTableSortLabel
                    active={orderBy === 'organizationName'}
                    direction={orderBy === 'organizationName' ? order : 'asc'}
                    onClick={() => handleRequestSort('organizationName')}
                  >
                    {t('ADMIN_TRANSACTION_ORGANIZATION')}
                  </StyledTableSortLabel>
                </CustomTableFontCell>
                <CustomTableFontCell>
                  <StyledTableSortLabel
                    active={orderBy === 'project'}
                    direction={orderBy === 'project' ? order : 'asc'}
                    onClick={() => handleRequestSort('project')}
                  >
                    {t('ADMIN_TRANSACTION_PROJECT')}
                  </StyledTableSortLabel>
                </CustomTableFontCell>
                <CustomTableFontCell>
                  <StyledTableSortLabel
                    active={orderBy === 'project_id'}
                    direction={orderBy === 'project_id' ? order : 'asc'}
                    onClick={() => handleRequestSort('project_id')}
                  >
                    {t('ADMIN_TRANSACTION_PROJECT_ID')}
                  </StyledTableSortLabel>
                </CustomTableFontCell>
                <CustomTableFontCell>
                  <StyledTableSortLabel
                    active={orderBy === 'user'}
                    direction={orderBy === 'user' ? order : 'asc'}
                    onClick={() => handleRequestSort('user')}
                  >
                    {t('ADMIN_TRANSACTION_USER')}
                  </StyledTableSortLabel>
                </CustomTableFontCell>
                <CustomTableFontCell>
                  <StyledTableSortLabel
                    active={orderBy === 'revision_id'}
                    direction={orderBy === 'revision_id' ? order : 'asc'}
                    onClick={() => handleRequestSort('revision_id')}
                  >
                    {t('ADMIN_TRANSACTION_REVISION')}
                  </StyledTableSortLabel>
                </CustomTableFontCell>
                <CustomTableFontCell>
                  <StyledTableSortLabel
                    active={orderBy === 'points'}
                    direction={orderBy === 'points' ? order : 'asc'}
                    onClick={() => handleRequestSort('points')}
                  >
                    {t('ADMIN_TRANSACTION_POINTS')}
                  </StyledTableSortLabel>
                </CustomTableFontCell>
                <CustomTableFontCell>
                  <StyledTableSortLabel
                    active={orderBy === 'type'}
                    direction={orderBy === 'type' ? order : 'asc'}
                    onClick={() => handleRequestSort('type')}
                  >
                    {t('ADMIN_TRANSACTION_TYPE')}
                  </StyledTableSortLabel>
                </CustomTableFontCell>
                <CustomTableFontCell>
                  {t('ADMIN_TRANSACTION_CANCELLATION_REASON')}ok
                </CustomTableFontCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions?.data?.length === 0 ? (
                <TableRow>
                  <CustomTableFontCell colSpan={9} align="center">
                    {t('ADMIN_TRANSACTION_NO_TRANSACTIONS')}
                  </CustomTableFontCell>
                </TableRow>
              ) : (
                transactions?.data?.map((transaction) => (
                  <TableRow key={transaction.id}>
                    <TableCell>{formatDate(transaction?.createdAt || '')}</TableCell>
                    <TableCell>{transaction.organizationName}</TableCell>
                    <TableCell>{transaction.project || '-'}</TableCell>
                    <TableCell>{transaction.project_id || '-'}</TableCell>
                    <TableCell>{transaction.user || '-'}</TableCell>
                    <TableCell>{transaction.revision_id || '-'}</TableCell>
                    <TableCell>{formatNumber(transaction?.points)}</TableCell>
                    <TableCell>
                      <TransactionTypeChip type={transaction.type} />
                    </TableCell>
                    <TableCell>
                      
                     {transaction.cancellationReason || '-'}
                     </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[10, 20, 50]}
            component="div"
            count={transactions?.paging?.total || 0}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            labelRowsPerPage={t('ADMIN_TRANSACTION_ROWS_PER_PAGE')}
            labelDisplayedRows={({ from, to, count }) =>
              `${from}-${to} ${t('ADMIN_TRANSACTION_OF')} ${count}`
            }
          />
        </TableContainer>
    
    
    
    </Box>
  );
};

export default AdminTransactionsPage; 